/**
 * Display-only localisation for canonical marketplace entities.
 *
 * Canonical values remain the sole source for queries, slugs, IDs and URLs.
 * When a verified alias is supplied by a view or local dataset, this helper
 * selects it for the active language; otherwise it returns the canonical name.
 */
import { STATE_NAME_TRANSLATIONS } from '../data/indianStateNames';
import { FACTORY_NAME_TRANSLATIONS } from '../data/factoryNameTransliterations';

const supportedLanguages = new Set(['hi', 'mr', 'gu', 'ta', 'te', 'kn']);

export const localizedDisplayName = (canonicalName, language, aliases) => {
  if (!canonicalName || !supportedLanguages.has(language)) return canonicalName || '';

  const normalizedLanguage = language.split('-')[0];
  const alias = aliases?.[normalizedLanguage]
    || aliases?.[`display_name_${normalizedLanguage}`]
    || aliases?.[`name_${normalizedLanguage}`]
    || aliases?.[`${normalizedLanguage}_name`];

  return typeof alias === 'string' && alias.trim() ? alias.trim() : canonicalName;
};

export const localizedEntityName = (record, canonicalField, language) => {
  if (!record) return '';
  const canonicalName = record[canonicalField] || '';
  const aliases = record.localized_names || record.localizedNames || record.display_names || record.displayNames;

  return localizedDisplayName(canonicalName, language, aliases || record);
};

export const hasLocalizedDisplayName = (record, canonicalField, language) => (
  localizedEntityName(record, canonicalField, language) !== (record?.[canonicalField] || '')
);

// Factory/mill display name. Checks, in order: a verified alias on the
// record itself (future-proof - none of the current Supabase queries return
// one, but this keeps the door open without another resolver rewrite), then
// the static transliteration table in src/data/factoryNameTransliterations.js
// (see that file's header for how it was generated and its confidence tiers),
// then falls back to the canonical English name.
export const localizedFactoryName = (record, language, canonicalField = 'canonical_name') => {
  if (!record) return '';
  const canonicalName = record[canonicalField] || '';
  const viaRecord = localizedEntityName(record, canonicalField, language);
  if (viaRecord !== canonicalName) return viaRecord;
  return localizedDisplayName(canonicalName, language, FACTORY_NAME_TRANSLATIONS[canonicalName]);
};

// Standard/official state & UT names - see src/data/indianStateNames.js.
export const localizedStateName = (canonicalState, language) =>
  localizedDisplayName(canonicalState, language, STATE_NAME_TRANSLATIONS[canonicalState]);

// No verified per-language district name source exists (checked: mi_canonical_districts
// has only name/slug columns, and no alias/translation table exists anywhere in the
// schema). Left empty rather than guessed - district names fall back to canonical
// English via localizedDisplayName until a verified source is available.
const DISTRICT_NAME_TRANSLATIONS = {};

export const localizedDistrictName = (canonicalDistrict, canonicalState, language) =>
  localizedDisplayName(canonicalDistrict, language, DISTRICT_NAME_TRANSLATIONS[canonicalState]?.[canonicalDistrict]);
